import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';

const baseStyle = 'flex items-center justify-center text-xs font-medium transition-shadow';

export const StartNode = memo(({ data, selected }) => (
  <div className={`w-14 h-14 rounded-full border-2 ${baseStyle} ${selected ? 'ring-2 ring-brand-400 ring-offset-2 ring-offset-zinc-950' : ''} bg-emerald-500/20 border-emerald-500 text-emerald-400`}>
    <Handle type="source" position={Position.Right} className="!w-2.5 !h-2.5 !bg-emerald-400 !border-emerald-600" />
    <span className="text-[10px] font-bold">START</span>
  </div>
));
StartNode.displayName = 'StartNode';

export const EndNode = memo(({ data, selected }) => (
  <div className={`w-14 h-14 rounded-full border-2 ${baseStyle} ${selected ? 'ring-2 ring-brand-400 ring-offset-2 ring-offset-zinc-950' : ''} bg-red-500/20 border-red-500 text-red-400`}>
    <Handle type="target" position={Position.Left} className="!w-2.5 !h-2.5 !bg-red-400 !border-red-600" />
    <span className="text-[10px] font-bold">END</span>
  </div>
));
EndNode.displayName = 'EndNode';

export const TaskNode = memo(({ data, selected }) => (
  <div className={`min-w-[140px] rounded-lg border-2 ${selected ? 'ring-2 ring-brand-400 ring-offset-2 ring-offset-zinc-950' : ''} bg-zinc-800 border-brand-500 shadow-md`}>
    <Handle type="target" position={Position.Left} className="!w-2.5 !h-2.5 !bg-brand-400 !border-brand-600" />
    <Handle type="source" position={Position.Right} className="!w-2.5 !h-2.5 !bg-brand-400 !border-brand-600" />
    <div className="px-3 py-1.5 border-b border-zinc-700 bg-brand-500/10">
      <span className="text-[9px] uppercase tracking-wider text-brand-400 font-bold">Task</span>
    </div>
    <div className="px-3 py-2">
      <p className="text-sm font-medium text-zinc-100 truncate">{data?.label || 'Task'}</p>
      {data?.description && <p className="text-[10px] text-zinc-500 mt-0.5 truncate">{data.description}</p>}
    </div>
  </div>
));
TaskNode.displayName = 'TaskNode';

export const ExclusiveGatewayNode = memo(({ data, selected }) => (
  <div className={`relative ${selected ? 'ring-2 ring-brand-400 ring-offset-2 ring-offset-zinc-950 rounded-sm' : ''}`}>
    <Handle type="target" position={Position.Left} className="!w-2.5 !h-2.5 !bg-amber-400 !border-amber-600" style={{ top: '50%' }} />
    <Handle type="source" position={Position.Right} className="!w-2.5 !h-2.5 !bg-amber-400 !border-amber-600" id="right" style={{ top: '50%' }} />
    <Handle type="source" position={Position.Top} className="!w-2.5 !h-2.5 !bg-amber-400 !border-amber-600" id="top" />
    <Handle type="source" position={Position.Bottom} className="!w-2.5 !h-2.5 !bg-amber-400 !border-amber-600" id="bottom" />
    <div className="w-16 h-16 rotate-45 bg-amber-500/20 border-2 border-amber-500 flex items-center justify-center">
      <span className="text-amber-400 -rotate-45 text-lg font-bold">X</span>
    </div>
    <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap">
      <span className="text-[9px] text-amber-400 font-medium">{data?.label || 'Choice'}</span>
    </div>
  </div>
));
ExclusiveGatewayNode.displayName = 'ExclusiveGatewayNode';

export const ParallelGatewayNode = memo(({ data, selected }) => (
  <div className={`relative ${selected ? 'ring-2 ring-brand-400 ring-offset-2 ring-offset-zinc-950 rounded-sm' : ''}`}>
    <Handle type="target" position={Position.Left} className="!w-2.5 !h-2.5 !bg-violet-400 !border-violet-600" style={{ top: '50%' }} />
    <Handle type="source" position={Position.Right} className="!w-2.5 !h-2.5 !bg-violet-400 !border-violet-600" id="right" style={{ top: '50%' }} />
    <Handle type="source" position={Position.Top} className="!w-2.5 !h-2.5 !bg-violet-400 !border-violet-600" id="top" />
    <Handle type="source" position={Position.Bottom} className="!w-2.5 !h-2.5 !bg-violet-400 !border-violet-600" id="bottom" />
    <div className="w-16 h-16 rotate-45 bg-violet-500/20 border-2 border-violet-500 flex items-center justify-center">
      <span className="text-violet-400 -rotate-45 text-2xl font-bold">+</span>
    </div>
    <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap">
      <span className="text-[9px] text-violet-400 font-medium">{data?.label || 'Split'}</span>
    </div>
  </div>
));
ParallelGatewayNode.displayName = 'ParallelGatewayNode';

export const SyncGatewayNode = memo(({ data, selected }) => (
  <div className={`relative ${selected ? 'ring-2 ring-brand-400 ring-offset-2 ring-offset-zinc-950 rounded-sm' : ''}`}>
    <Handle type="target" position={Position.Left} className="!w-2.5 !h-2.5 !bg-cyan-400 !border-cyan-600" id="left-t" style={{ top: '50%' }} />
    <Handle type="target" position={Position.Top} className="!w-2.5 !h-2.5 !bg-cyan-400 !border-cyan-600" id="top-t" />
    <Handle type="target" position={Position.Bottom} className="!w-2.5 !h-2.5 !bg-cyan-400 !border-cyan-600" id="bottom-t" />
    <Handle type="source" position={Position.Right} className="!w-2.5 !h-2.5 !bg-cyan-400 !border-cyan-600" />
    <div className="w-16 h-16 rotate-45 bg-cyan-500/20 border-2 border-cyan-500 flex items-center justify-center">
      <span className="text-cyan-400 -rotate-45 text-lg font-bold">=</span>
    </div>
    <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap">
      <span className="text-[9px] text-cyan-400 font-medium">{data?.label || 'Sync'}</span>
    </div>
  </div>
));
SyncGatewayNode.displayName = 'SyncGatewayNode';

export const MergeGatewayNode = memo(({ data, selected }) => (
  <div className={`relative ${selected ? 'ring-2 ring-brand-400 ring-offset-2 ring-offset-zinc-950 rounded-sm' : ''}`}>
    <Handle type="target" position={Position.Left} className="!w-2.5 !h-2.5 !bg-pink-400 !border-pink-600" id="left-t" style={{ top: '50%' }} />
    <Handle type="target" position={Position.Top} className="!w-2.5 !h-2.5 !bg-pink-400 !border-pink-600" id="top-t" />
    <Handle type="target" position={Position.Bottom} className="!w-2.5 !h-2.5 !bg-pink-400 !border-pink-600" id="bottom-t" />
    <Handle type="source" position={Position.Right} className="!w-2.5 !h-2.5 !bg-pink-400 !border-pink-600" />
    <div className="w-16 h-16 rotate-45 bg-pink-500/20 border-2 border-pink-500 flex items-center justify-center">
      <span className="text-pink-400 -rotate-45 text-lg font-bold">V</span>
    </div>
    <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 whitespace-nowrap">
      <span className="text-[9px] text-pink-400 font-medium">{data?.label || 'Merge'}</span>
    </div>
  </div>
));
MergeGatewayNode.displayName = 'MergeGatewayNode';

export const nodeTypes = {
  start: StartNode,
  end: EndNode,
  task: TaskNode,
  exclusive: ExclusiveGatewayNode,
  parallel: ParallelGatewayNode,
  sync: SyncGatewayNode,
  merge: MergeGatewayNode,
};
